import { Suspense, useMemo, useState, useEffect } from 'react'

import {
  FocusableAd,
  TopPerformingAds,
} from '../features/insights_hub/components/TopPerformingAds/TopPerformingAds'
import { AdDetailsModal } from '../features/insights_hub/components/AdDetailsModal/AdDetailsModal'
import { LoadingScreen } from '../features/insights_hub/components/LoadingScreen/LoadingScreen'
import {
  FilterBar,
} from '../features/insights_hub/components/FilterBar/FilterBar'
import { useGlobalState } from '../state/v2/store'
import { KpiDisplay, yyyymmdd } from '../display'
import { useAdsFilterMenuParams } from '../features/insights_hub/components/FilterMenu/useFilterMenuParams'
import { BottomPerformingAds } from '../features/insights_hub/components/BottomPerformingAds/BottomPerformingAds'
import { DateRangePickerValue, getValidatedDateRange } from '../features/insights_hub/components/FilterBar/DateRangeFilter/DateRangeFilter'
import { parse } from 'date-fns'
import AdPerformanceTable from '../features/insights_hub/components/AdPerformanceTable/AdPerformanceTable'
import { ToggleButton, ToggleButtonGroup } from '@mui/material'
import { ViewList, ViewModule } from '@mui/icons-material'
import { CookieType, cookies } from '../state/cookies'
import { useTrackingContext } from '../analytics/useTracking'
import { trackAdSummaryViewed, trackKpiChanged, trackDateRangeSelected, trackViewSwitched } from '../analytics/tracking'
import { CurrencyType } from '../features/insights_hub/components/TopPerformingAds/__generated__/TopPerformingAdsQuery.graphql'

interface AdsSummaryProps {
  tagsEnabled: boolean
  purchasesEnabled: boolean
  leadsEnabled: boolean
  hasCurrentMonthData: boolean
}

export function AdsSummary({ tagsEnabled, purchasesEnabled, leadsEnabled, hasCurrentMonthData }: AdsSummaryProps) {
  const [focusedAd, setFocusedAd] = useState<FocusableAd>()
  const [currencyType, setCurrencyType] = useState<CurrencyType>()
  const [view, setView] = useState(cookies.get(CookieType.ADS_VIEW) || 'grid')
  const handleViewChange = (event: React.ChangeEvent<{}>, newView: string) => {
    if (newView !== null) {
      setView(newView)
      cookies.set(CookieType.ADS_VIEW.valueOf(), newView)
      trackViewSwitched({ brand_id: brandId, account_id: ctx.accountId, view: newView === 'grid' ? 'visual' : 'list' }, ctx)
    }
  };

  const selectedBrands = useGlobalState((s) => s.brand)
  const ctx = useTrackingContext()
  const brandId = selectedBrands?.[0]?.id ?? ''
  const kpiFilterOptions = useMemo(() => {
    if (!selectedBrands) return []

    return Array.from(new Set(selectedBrands.flatMap((b) => b.availableKpis))).map((kpi) => ({
      label: KpiDisplay[kpi],
      value: kpi,
    }))
  }, [selectedBrands])

  const [searchParams, setSearchParams] = useAdsFilterMenuParams()
  let selectedKpiOption = kpiFilterOptions.find(
    (opt) => opt.value === searchParams.adKpi
  )

  useEffect(() => {
    if (!selectedKpiOption && kpiFilterOptions.length > 0) {
        setSearchParams({ adKpi: kpiFilterOptions[0].value }, 'pushIn');
    }
}, [kpiFilterOptions, selectedKpiOption, setSearchParams]);


  const selectedDateRange: DateRangePickerValue = getValidatedDateRange(
    searchParams.performanceDateGte,
    searchParams.performanceDateLte,
    hasCurrentMonthData
  );
  const { adKpi, performanceDateGte, performanceDateLte, deduplicateAdNames, topAdMetricsFilters, bottomAdMetricsFilters } = searchParams;

  const [key, setKey] = useState(0);

  useEffect(() => {
    setKey((prevKey) => prevKey + 1);
  }, [adKpi, performanceDateGte, performanceDateLte, deduplicateAdNames, topAdMetricsFilters, bottomAdMetricsFilters]);

  useEffect(() => {
    if (!adKpi || !brandId) return
    trackAdSummaryViewed({ brand_id: brandId, account_id: ctx.accountId, kpi: adKpi }, ctx)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adKpi, brandId])
  const showAdsTable = view === 'table' && tagsEnabled
  return (
    <>
      <AdDetailsModal
        isOpen={!!focusedAd}
        close={() => setFocusedAd(undefined)}
        fragmentKey={focusedAd}
        tagsEnabled={tagsEnabled}
        purchasesEnabled={purchasesEnabled}
        currencyType={currencyType || "USD"}
      />
      <FilterBar>
        <FilterBar.FilterItemWrapper>
          <FilterBar.ItemLabel>Target KPI</FilterBar.ItemLabel>
          <FilterBar.SelectFilter
            name="kpi"
            options={kpiFilterOptions}
            getOptionValue={(opt) => opt.value}
            renderOptionLabel={(opt) => opt.label}
            value={selectedKpiOption}
            handleChange={(opt) => {
              if (opt?.value && opt.value !== adKpi) {
                trackKpiChanged({ brand_id: brandId, account_id: ctx.accountId, kpi: opt.value, previous_kpi: adKpi ?? undefined }, ctx)
              }
              setSearchParams({ adKpi: opt?.value }, 'pushIn')
            }}
          />
        </FilterBar.FilterItemWrapper>
        <FilterBar.FilterItemWrapper>
          <FilterBar.ItemLabel>Performance Dates</FilterBar.ItemLabel>
          <FilterBar.DateRangeFilter
            name="performanceDates"
            value={selectedDateRange}
            handleChange={(range) => {
              const performanceDateGte = range[0] ? yyyymmdd(range[0]) : null
              const performanceDateLte = range[1] ? yyyymmdd(range[1]) : null
              if (performanceDateGte && performanceDateLte) {
                trackDateRangeSelected({ brand_id: brandId, account_id: ctx.accountId, kpi: adKpi ?? '', surface: 'ad_summary' }, ctx)
              }
              setSearchParams(
                {
                  performanceDateGte,
                  performanceDateLte,
                },
                'pushIn'
              )
            }}
            hasCurrentMonthData={hasCurrentMonthData}
          />
        </FilterBar.FilterItemWrapper>
        {!showAdsTable ? (
          <FilterBar.FilterItemWrapper>
            <FilterBar.ItemLabel>Collapse/Expand on Ad Name</FilterBar.ItemLabel>
            <FilterBar.CheckBoxFilter
              name="deduplicateByAdName"
              value={searchParams.deduplicateAdNames !== "0"}
              handleChange={(val) => {
                setSearchParams({ deduplicateAdNames: val ? "1" : "0" }, 'pushIn')
              }}
            />
          </FilterBar.FilterItemWrapper>
        ) : <></>}
        {tagsEnabled &&
          <div className="ml-auto flex items-baseline py-2" >
            <ToggleButtonGroup value={view} exclusive onChange={handleViewChange}>
              <ToggleButton value="grid"><ViewModule /></ToggleButton>
              <ToggleButton value="table"><ViewList /></ToggleButton>
            </ToggleButtonGroup>
          </div>
        }
      </FilterBar>
      {showAdsTable ? (<Suspense fallback={<LoadingScreen />}>
        <div className="px-8 py-12">
          <AdPerformanceTable key={key} purchasesEnabled={purchasesEnabled} leadsEnabled={leadsEnabled} />
        </div>
      </Suspense>
      ) : (
        <>
          <div className="px-8 py-12">
            <Suspense fallback={<LoadingScreen />}>
              <div>
                <TopPerformingAds key={key} focusAd={(ad: FocusableAd, currencyType: CurrencyType) =>{
                  setFocusedAd(ad)
                  setCurrencyType(currencyType)
                }} purchasesEnabled={purchasesEnabled} leadsEnabled={leadsEnabled} />
              </div>
            </Suspense>
          </div>
          <div className="px-8 py-12">
            <Suspense fallback={<LoadingScreen />}>
              <div>
                <BottomPerformingAds key={key} focusAd={(ad: FocusableAd, currencyType: CurrencyType) =>{
                  setFocusedAd(ad)
                  setCurrencyType(currencyType)
                }} purchasesEnabled={purchasesEnabled} leadsEnabled={leadsEnabled} />
              </div>
            </Suspense>
          </div>
        </>
      )

      }
    </>
  )
}
